import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';
import type { SelectInputOption } from '@/components/inputs/SelectInput';

const geographicService = createApi({
  reducerPath: 'commerceGeographicService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: ['CommerceGeographic'],
  endpoints: (builder) => ({
    getCountries: builder.query<
      SelectInputOption[],
      { limit?: number; offset?: number } | void
    >({
      query: (args) => ({
        url: '/countries',
        params: {
          limit: args?.limit ?? 200,
          offset: args?.offset ?? 0,
        },
      }),
      transformResponse: (response: any): SelectInputOption[] => {
        const data = response?.data ?? response ?? {};
        const rows = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data.rows)
            ? data.rows
            : Array.isArray(data)
              ? data
              : [];
        return rows
          .map((c: any) => ({
            value: String(c.id ?? ''),
            label: String(c.name ?? ''),
          }))
          .filter((o: SelectInputOption) => o.value && o.label);
      },
      providesTags: ['CommerceGeographic'],
    }),
    getStates: builder.query<
      SelectInputOption[],
      { countryId?: string; limit?: number; offset?: number } | void
    >({
      query: (args) => ({
        url: '/states-or-departments',
        params: {
          limit: args?.limit ?? 200,
          offset: args?.offset ?? 0,
          ...(args?.countryId ? { countryId: args.countryId } : {}),
        },
      }),
      transformResponse: (response: any): SelectInputOption[] => {
        const data = response?.data ?? response ?? {};
        const rows = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data.rows)
            ? data.rows
            : Array.isArray(data)
              ? data
              : [];
        return rows
          .map((s: any) => ({
            value: String(s.id ?? ''),
            label: String(s.name ?? ''),
          }))
          .filter((o: SelectInputOption) => o.value && o.label);
      },
      providesTags: ['CommerceGeographic'],
    }),
    getCities: builder.query<
      SelectInputOption[],
      { stateId?: string; limit?: number; offset?: number } | void
    >({
      query: (args) => ({
        url: '/cities',
        params: {
          limit: args?.limit ?? 200,
          offset: args?.offset ?? 0,
          ...(args?.stateId ? { stateOrDepartmentId: args.stateId } : {}),
        },
      }),
      transformResponse: (response: any): SelectInputOption[] => {
        const data = response?.data ?? response ?? {};
        const rows = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data.rows)
            ? data.rows
            : Array.isArray(data)
              ? data
              : [];
        return rows
          .map((c: any) => ({
            value: String(c.id ?? ''),
            label: String(c.name ?? ''),
          }))
          .filter((o: SelectInputOption) => o.value && o.label);
      },
      providesTags: ['CommerceGeographic'],
    }),
    getNeighborhoodsByCity: builder.query<
      SelectInputOption[],
      { cityId: string }
    >({
      query: ({ cityId }) => ({
        url: `/neighborhoods/by-city/${cityId}`,
      }),
      transformResponse: (response: any): SelectInputOption[] => {
        const data = response?.data ?? response ?? {};
        const rows = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data.rows)
            ? data.rows
            : Array.isArray(data)
              ? data
              : [];
        return rows
          .map((n: any) => ({
            value: String(n.id ?? ''),
            label: String(n.name ?? ''),
          }))
          .filter((o: SelectInputOption) => o.value && o.label);
      },
      providesTags: ['CommerceGeographic'],
    }),
  }),
});

export default geographicService;
export const {
  useGetCountriesQuery,
  useGetStatesQuery,
  useGetCitiesQuery,
  useGetNeighborhoodsByCityQuery,
} = geographicService;
